/**
 * Reads the current user's session uuid from localStorage and validates it.
 *
 * Throws `Error("Missing uuidUser in session — user is not authenticated")`
 * if the value is missing or empty. The throw is intentional: returning
 * `null`/`""` would propagate as the literal strings `"null"` / `""` in URLs
 * and bypass server-side audit checks (cf. backend `uuidUserString` refine
 * in `src/router/settings/generalSetting/fidelity/index.ts`).
 *
 * Calling convention:
 *
 * - **At module/component init** (e.g. inside a hook body, or near the top
 *   of a `function MyPage()` component): an uncaught throw will bubble to
 *   the nearest React error boundary and unmount the subtree. That's the
 *   correct UX for a missing session — the user should not see a half-rendered
 *   page. Make sure an error boundary is mounted above the page route.
 *
 * - **Inside an async event handler** (e.g. submit, archive, associate):
 *   wrap in try/catch. On catch, surface `errorToast(t("Session expired"))`
 *   and either redirect to `/login` or close the in-flight modal. Letting
 *   the throw escape mid-modal interrupts the UX silently.
 *
 * Known gap (ESS-3549 audit, 11 call sites under `src/pages/SettingGeneral/Fidelity/`):
 * none of the current callers wrap this in a try/catch. Tracked in
 * `docs/ESS-3549-followups.md` — to address in a separate ticket.
 */
export function getUuidUser(): string {
    const value = localStorage.getItem("uuidUser");
    if (value === null || value === "") {
        throw new Error(
            "Missing uuidUser in session — user is not authenticated"
        );
    }
    return value;
}
